import { useLayoutEffect, useRef, type RefObject } from 'react';
import { getFont } from '../designs/fonts';
import type { Note } from '../storage/schemas';

export const FIT_MIN = 3.2;
// Reference: text never grows past its normal size (≈7% of the card width); it only shrinks to fit.
export const FIT_MAX = 7;

/**
 * Shrink-to-fit: find the largest font size (in % of card width) at which all lines fit inside the card,
 * then report it so it is stored with the note (thumbnails, previews and exports reuse it without measuring).
 */
export function useAutoFit(textRef: RefObject<HTMLElement | null>, note: Note, fontId: string, onFit: (scale: number) => void, enabled: boolean) {
  const lastReported = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || !enabled || !note.style.autoSize) return;
    const fontFactor = getFont(fontId).scale;
    const measure = () => {
      if (!el.isConnected || el.clientHeight === 0) return;
      const original = el.style.getPropertyValue('--fs');
      const fits = (scale: number) => {
        el.style.setProperty('--fs', `${(scale * fontFactor).toFixed(3)}cqw`);
        return el.scrollHeight <= el.clientHeight + 1;
      };
      let lo = FIT_MIN;
      let hi = FIT_MAX;
      if (fits(hi)) lo = hi;
      else {
        for (let i = 0; i < 7; i++) {
          const mid = (lo + hi) / 2;
          if (fits(mid)) lo = mid;
          else hi = mid;
        }
      }
      if (original) el.style.setProperty('--fs', original);
      else el.style.removeProperty('--fs');
      const rounded = Math.round(lo * 20) / 20;
      if (Math.abs(rounded - note.style.fontScale) >= 0.05 && rounded !== lastReported.current) {
        lastReported.current = rounded;
        onFit(rounded);
      }
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null;
    ro?.observe(el.parentElement ?? el);
    // Web fonts load lazily; re-measure when they arrive.
    let alive = true;
    void document.fonts?.ready.then(() => alive && measure());
    return () => {
      alive = false;
      ro?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note.lines, note.style.autoSize, note.style.lineHeight, note.style.compact, fontId, enabled]);
}
